import { Row, Col, InputGroup, Input, InputGroupText } from "reactstrap";
import React from "react";
import classNames from "classnames";
import ReactTooltip from "react-tooltip";
import type { ISortOption } from "../../components/sortDropdown";
import Dashboard from "../../components/dashboard";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";
import ISortByType from "../../../types/services/ISortByType";
import {
  serviceCaller,
  withModals,
  ServiceCallerProps,
  ModalProps,
  withLoginRedirect,
} from "../../composition";
import { firebaseConnect, isEmpty, isLoaded } from "react-redux-firebase";
import { connect } from "react-redux";
import get from "lodash.get";
import isEqual from "lodash.isequal";
import ISegment from "../../../types/services/ISegment";
import IKeyword from "../../../types/services/IKeyword";
import { buildKeywordRows, KeywordRow } from "./keywordRows";
import { getParsedDate } from "../../../utils/helpers";
import { ScaleLoader } from "react-spinners";
import * as commonActions from "../../../utils/actions/common";
import ISelectionUpdate from "../../../types/ISelectionUpdate";
import { isCurrentUserOnlyCollaborator } from "../../../utils/roleCheck";
import IColumnName from "../../../types/IColumnName";
import {
  applyColumnPrefs,
  TableIds,
  toggleSavedColumn,
} from "../../../utils/tablePrefs";
import firebase from "firebase/compat/app";
import App = firebase.app.App;

interface Props extends ServiceCallerProps, ModalProps {
  segments: Array<ISegment>;
  keywords: Array<IKeyword>;
  firebase: App;
  refreshDashboard: () => void;
  hideSelection: () => void;
}

interface State {
  sortBy: ISortByType;
  searchText: string;
  searchTextUsed: string;
  selectAll: boolean;
  sortAsc: boolean;
  showExportModal: boolean;
  isLoaded: boolean;
  keywordList: Array<KeywordRow>;
  selectedIds: string[];
  suppressedIds: string[];
  columnNames: Array<IColumnName>;
}

const sortOptions: ISortOption[] = [
  { key: "NAME", show: "Keyword", colName: "keywordText" },
  { key: "SEGMENT", show: "Segment", colName: "segmentLabel" },
  { key: "DONOR_COUNT", show: "# of Contacts", colName: "donorCount" },
  { key: "CREATED", show: "Date Created", colName: "createdOnStr" },
  { key: "UPDATED", show: "Last Update", colName: "modifiedOnStr" },
];

class NewKeyboardsPage extends React.PureComponent<Props, State> {
  defaultColumnNames = [
    { key: "Keyword", isDisabled: false },
    { key: "Segment", isDisabled: false },
    { key: "# of Contacts", isDisabled: false },
    { key: "Date Created", isDisabled: false },
    { key: "Last Update", isDisabled: false },
  ];

  columnData = [
    "keywordText",
    "segmentLabel",
    "donorCount",
    "createdOnStr",
    "modifiedOnStr",
  ];

  state = {
    sortBy: sortOptions[0],
    searchText: "",
    searchTextUsed: "",
    selectAll: false,
    sortAsc: true,
    showExportModal: false,
    isLoaded: false,
    keywordList: null,
    selectedIds: [],
    suppressedIds: [],
    columnNames: applyColumnPrefs(
      TableIds.simpleTextingKeywords,
      this.defaultColumnNames
    ),
  };

  searchTimeout = null;

  componentWillUnmount() {
    if (window) {
      window.removeEventListener("resize", this.handleWindowSize);
    }
    if (this.searchTimeout && window) {
      window.clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }

  handleWindowSize = () => {
    if (window) {
      if (window.innerWidth < 768) {
        this.setState({
          columnNames: applyColumnPrefs(TableIds.simpleTextingKeywords, [
            { key: "Keyword", isDisabled: false },
            { key: "Segment", isDisabled: false },
            { key: "# of Contacts", isDisabled: false },
            { key: "Date Created", isDisabled: true },
            { key: "Last Update", isDisabled: true },
          ]),
        });
      } else {
        this.setState({
          columnNames: applyColumnPrefs(
            TableIds.simpleTextingKeywords,
            this.defaultColumnNames
          ),
        });
      }
    }
  };

  componentDidMount() {
    this.props.hideSelection();

    if (this.props.segments && this.props.keywords) {
      this.fillNewList();
    }
    if (window) {
      this.handleWindowSize();
      window.addEventListener("resize", this.handleWindowSize);
    }
    if (this.props.currentCharity) {
      if (isCurrentUserOnlyCollaborator(this.props.currentCharity)) {
        this.props.navigate("/viewsparks");
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.state.isLoaded && this.props.segments && this.props.keywords) {
      this.fillNewList();
      return;
    }
    if (
      !isEqual(this.props.segments, prevProps.segments) ||
      !isEqual(this.props.keywords, prevProps.keywords) ||
      this.state.searchTextUsed !== prevState.searchTextUsed
    ) {
      this.fillNewList();
    }
    if (this.props.currentCharity.id !== prevProps.currentCharity.id) {
      this.setState({
        isLoaded: false,
        keywordList: null,
      });
    }
    if (this.props.currentCharity) {
      if (isCurrentUserOnlyCollaborator(this.props.currentCharity)) {
        this.props.navigate("/viewsparks");
      }
    }
  }

  fillNewList = () => {
    if (!this.props.segments || !this.props.keywords) return;

    let newkeywordList = buildKeywordRows(
      this.props.segments as KeywordRow[],
      this.props.keywords,
      this.props.currentCharity.id
    );
    if (this.state.searchTextUsed) {
      const query = this.state.searchTextUsed.toLowerCase();
      newkeywordList = newkeywordList.filter(segment =>
        segment.keywordText.toLowerCase().includes(query) ||
        segment.segmentLabel.toLowerCase().includes(query)
      );
    }
    newkeywordList = this.sortSegments(this.state.sortBy, newkeywordList);

    this.setState(
      {
        keywordList: newkeywordList,
        isLoaded: true,
      },
      () => {
        this.props.refreshDashboard();
      }
    );
  };

  sortSegments(
    newOption: ISortOption,
    newkeywordList: Array<KeywordRow>
  ): Array<KeywordRow> {
    switch (newOption.colName) {
      case "keywordText":
        newkeywordList.sort((a, b) => {
          if (a.keywordText < b.keywordText) return -1;
          if (a.keywordText > b.keywordText) return 1;
          return 0;
        });
        break;
      case "segmentLabel":
        newkeywordList.sort((a, b) => {
          if (a.segmentLabel < b.segmentLabel) return -1;
          if (a.segmentLabel > b.segmentLabel) return 1;
          return 0;
        });
        break;
      case "donorCount":
        newkeywordList.sort((a, b) => {
          return (a.donorCount || 0) - (b.donorCount || 0);
        });
        break;
      case "createdOnStr":
        newkeywordList.sort((a, b) => {
          return a.createdOn - b.createdOn;
        });
        break;
      case "modifiedOnStr":
        newkeywordList.sort((a, b) => {
          return a.modifiedOn - b.modifiedOn;
        });
        break;
      default:
        newkeywordList.sort(function(a, b) {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });
    }
    if (!this.state.sortAsc) {
      newkeywordList.reverse();
    }
    return newkeywordList;
  }

  handleColumnHeaderSort = (accessor: string) => {
    const option = sortOptions.find(o => o.colName === accessor);
    if (!option) {
      return;
    }
    this.setState(
      prev => {
        const same = prev.sortBy.colName === option.colName;
        return {
          sortBy: option,
          sortAsc: same ? !prev.sortAsc : false,
        };
      },
      () => this.fillNewList()
    );
  };

  selectAllCheck = e => {
    this.setState({
      selectAll: e.target.checked,
    });
  };

  searchTextChange = e => {
    const newText: string = e.target.value;

    if (this.searchTimeout && window) {
      window.clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
    this.setState({
      searchText: newText,
      selectAll: false,
    });

    let timeout = 200;
    if (newText && newText.length < 3) {
      timeout = 1500;
    }

    if (window) {
      this.searchTimeout = window.setTimeout(() => {
        this.setState({
          searchTextUsed: newText,
          selectAll: false,
        });
      }, timeout);
    }
  };

  clearSearch = () => {
    this.setState({ searchText: "", searchTextUsed: "" }, () => {
      this.props.refreshDashboard();
    });
  };

  showDonors = (segment: KeywordRow) => {
    const segmentId = segment.targetSegmentId || segment.id;
    if (segment.linkedOrdinaryList) {
      this.props.navigate(`/segments/${segmentId}/contacts/segment`);
    } else {
      this.props.navigate(`${segmentId}/contacts/keyword`);
    }
  };

  selectionUpdate = (update: ISelectionUpdate) => {
    this.setState({
      selectedIds: update.selectedIds,
      suppressedIds: update.suppressedIds,
    });
  };

  disableColumn = index => {
    const copy = toggleSavedColumn(
      TableIds.simpleTextingKeywords,
      this.state.columnNames,
      index
    );
    this.setState(
      {
        columnNames: copy,
      },
      () => {
        this.props.columnsModalChange(this.state.columnNames);
      }
    );
  };

  changeColumns = () => {
    this.props.toggleColumnsModal(this.disableColumn, this.state.columnNames);
  };

  getSegments = () =>
    Promise.resolve({
      result: this.state.keywordList,
      totalAmount: this.state.keywordList.length,
      pageAmount: 1,
      time: Date.now(),
    });

  extraColumns = [
    {
      id: "users",
      hasTooltip: true,
      tooltipText: "View contacts (linked lists show all members)",
      type: "icon",
      width: 45,
      faIcon: "fa-user-circle",
      className: "customColUsersIcon",
      showFunctions: this.showDonors,
    },
  ];

  renderDashboard() {
    if (
      this.props.serviceAccessToken &&
      this.state.keywordList &&
      (this.state.keywordList.length >= 0 ||
        this.state.searchText.length > 0) &&
      this.state.isLoaded
    ) {
      return (
        <Dashboard
          tableId={TableIds.simpleTextingKeywords}
          columnNames={this.state.columnNames}
          columnData={this.columnData}
          extraColumns={this.extraColumns}
          checkCol={false}
          selectionUpdate={this.selectionUpdate}
          hideSelection={true}
          sortBy={this.state.sortBy.colName}
          selectAll={this.state.selectAll}
          sortAsc={this.state.sortAsc}
          columnHeaderSort={{
            sortAccessor: this.state.sortBy.colName,
            sortAscending: this.state.sortAsc,
            sortableAccessors: sortOptions.map(o => o.colName),
            onSort: this.handleColumnHeaderSort,
          }}
          searchText={this.state.searchTextUsed}
          filtered={!!this.state.searchTextUsed}
          currentCharity={this.props.currentCharity}
          showPagination={false}
          noDataText={"No Keywords Found"}
          getResponse={this.getSegments}
        />
      );
    } else {
      return (
        <div className={s.loader}>
          <ScaleLoader color={"#1f65b1"} loading />
        </div>
      );
    }
  }

  render() {
    return (
      <main className={s.main}>
        <div className={s.container}>
          <Row className={s.marginBot}>
            <Col xs={12} className={classNames(s.colMargin, s.noPadding)}>
              <div className={s.keywordsToolbarFlex}>
                <div className={s.toolbarLeft}>
                  <div
                    className={s.inlineDots}
                    onClick={this.changeColumns}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        this.changeColumns();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    data-tip
                    data-for="simple-texting-keywords-column-mgmt"
                    aria-label="Column management"
                  >
                    <i className={"fa fa-columns " + s.dotIcon}>&nbsp;</i>
                  </div>
                  <ReactTooltip
                    id="simple-texting-keywords-column-mgmt"
                    effect="solid"
                    place="top"
                  >
                    Column management
                  </ReactTooltip>
                </div>
                <div className={s.toolbarSearchGrow}>
                  <InputGroup className={s.searchInputFullWidth}>
                    <InputGroupText className={s.inputGroup}>
                      <i className={classNames("fa fa-search", s.searchIcon)}>
                        &nbsp;
                      </i>
                    </InputGroupText>
                    <Input
                      id="searchText"
                      placeholder={"Search"}
                      value={this.state.searchText}
                      onChange={this.searchTextChange}
                      className={s.text}
                    />
                    <InputGroupText className={s.inputGroupRight}>
                      <i
                        className={classNames("fa fa-times", s.closeIcon)}
                        onClick={this.clearSearch}
                      >
                        &nbsp;
                      </i>
                    </InputGroupText>
                  </InputGroup>
                </div>
              </div>
            </Col>
          </Row>
          <Row>{this.renderDashboard()}</Row>
        </div>
      </main>
    );
  }
}

const mapStateToProps = state => {
  const { firebase, user } = state;
  return {
    firebase: firebase,
    keywords: isLoaded(firebase.data.smsKeywords)
      ? isEmpty(firebase.data.smsKeywords)
        ? []
        : (get(firebase, "ordered.smsKeywords", []) as Array<{ key: string; value: IKeyword }>).map(
            keyword => ({ ...keyword.value, id: keyword.key })
          )
      : null,
    segments:
      isLoaded(firebase.data[user.currentCharity.id]) &&
      isLoaded(firebase.data[user.currentCharity.id].segments)
        ? !isEmpty(firebase.data[user.currentCharity.id].segments)
          ? get(firebase, `ordered.${user.currentCharity.id}.segments`).map(
              segment => {
                let filters = "";
                if (segment.value.recency) {
                  filters +=
                    (segment.value.recency.minValue
                      ? segment.value.recency.minValue / 30
                      : "0") + " - ";
                  filters +=
                    (segment.value.recency.maxValue
                      ? segment.value.recency.maxValue / 30
                      : "60+") + " months, ";
                }
                if (segment.value.frequency) {
                  filters +=
                    (segment.value.frequency.minValue
                      ? segment.value.frequency.minValue
                      : "0") + " - ";
                  filters +=
                    (segment.value.frequency.maxValue
                      ? segment.value.frequency.maxValue
                      : "100") + " gifts, ";
                }
                if (segment.value.amount) {
                  filters +=
                    "$" +
                    (segment.value.amount.minValue
                      ? segment.value.amount.minValue
                      : "0.00") +
                    " - ";
                  filters += segment.value.amount.maxValue
                    ? "$" + segment.value.amount.maxValue
                    : "Any amount";
                }
                if (filters.endsWith(", ")) {
                  filters = filters.substring(0, filters.length - 2);
                  filters += "\n";
                }
                return {
                  ...segment.value,
                  name: segment.value.name + "\n" + filters,
                  id: segment.key,
                  createdOn: segment.value.createdOn
                    ? segment.value.createdOn
                    : 0,
                  modifiedOn: segment.value.modifiedOn
                    ? segment.value.modifiedOn
                    : 0,
                  createdOnStr: segment.value.createdOn
                    ? getParsedDate(
                        new Date(segment.value.createdOn).toString(),
                        false,
                        "MM/DD/YYYY, hh:mm a"
                      )
                    : "Not available",
                  modifiedOnStr: segment.value.modifiedOn
                    ? getParsedDate(
                        new Date(segment.value.modifiedOn).toString(),
                        false,
                        "MM/DD/YYYY, hh:mm a"
                      )
                    : "Not available",
                };
              }
            )
          : []
        : null,
  };
};

const mapDispatchToProps = dispatch => ({
  refreshDashboard: () => {
    dispatch(commonActions.refreshDashboard());
  },
  hideSelection: () => {
    dispatch(commonActions.hideSelection());
  },
});

export default serviceCaller(
  withLoginRedirect(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(
      withModals(
        firebaseConnect((_, store) => {
          return store.getState().user.currentCharity.id !== ""
            ? [
                {
                  path: `${store.getState().user.currentCharity.id}/segments`,
                },
                {
                  path: "smsKeywords",
                  queryParams: [
                    "orderByChild=charityId",
                    `equalTo=${store.getState().user.currentCharity.id}`,
                  ],
                },
              ]
            : [];
        })(withStyles(s)(NewKeyboardsPage))
      )
    )
  )
);
